import { Http } from 'lib'

import {
  setGraduateGpaValue,
  setInitiativeData,
  setIsLoadingInitiative,
  setIsSubmittingInitiative,
  setQuestionAnswer,
  setQuestionChoice,
  setUndergraduateGpaValue,
  setIsWithdrawing,
  reset,
  setDoesNotApplyGraduateGpa,
  setDoesNotApplyUndergraduateGpa,
  InitiativeManagementAction
} from './store/Actions'

import ServiceProviderBase from 'lib/ServiceProviderBase'

import reducer from './store/Reducer'

import apiUrls from 'config/constants/api-urls-addresses'
import IInitiativeSubmissionData from 'types/IInitiativeSubmissionData'
import IUserData from 'types/sign-in/IUserData'
import { InitiativeManagementState, initiativeManagementInitialState } from './store/State'
import { IUpdateSubmissionDataPayload, IUpdateInitiativeAnswer } from './Interfaces'

export class InitiativeManagementService extends ServiceProviderBase<
  InitiativeManagementState,
  InitiativeManagementAction
> {
  constructor() {
    super(initiativeManagementInitialState)
  }

  reduce(state: InitiativeManagementState, action: InitiativeManagementAction): InitiativeManagementState {
    return reducer(state, action)
  }

  parseInitiativeDataToAnswerState(initiativeData: IInitiativeSubmissionData): IUpdateInitiativeAnswer {
    return {
      gpaValues: {
        graduateGpa: initiativeData.graduateGpa ? parseFloat(initiativeData.graduateGpa.toString()) : null,
        doesNotApplyForGraduateGpa: !initiativeData.graduateGpa,
        undergraduateGpa: initiativeData.undergraduateGpa
          ? parseFloat(initiativeData.undergraduateGpa.toString())
          : null,
        doesNotApplyForUndergraduateGpa: !initiativeData.undergraduateGpa
      },
      questions: initiativeData.questions.reduce((acc, question) => {
        return Object.assign({}, acc, { [question.id]: { answer: question.answer, choices: question.choices } })
      }, {})
    }
  }

  async getInitiativeData(enterpriseId: string, initiativeId: number): Promise<string | null> {
    this.dispatch(setIsLoadingInitiative(true))

    const [data, , error] = await Http.get<IInitiativeSubmissionData>(
      apiUrls.enterprise.loadInitiativeForEdition(enterpriseId, initiativeId)
    )

    if (error) {
      this.dispatch(setIsLoadingInitiative(false))
      return error
    }

    this.dispatch(setInitiativeData(data, this.parseInitiativeDataToAnswerState(data)))
    return null
  }

  parseAndValidateGpaValue(value: string): [boolean, number] {
    const numberValue = parseFloat(value)
    return [!isNaN(numberValue) && 0 < numberValue && numberValue <= 4, numberValue]
  }

  setUndergraduateGpaValue(gpaValue: string) {
    const [isValid, value] = this.parseAndValidateGpaValue(gpaValue)
    if (isValid) {
      this.dispatch(setUndergraduateGpaValue(value))
    }
  }

  setGraduateGpaValue(gpaValue: string) {
    const [isValid, value] = this.parseAndValidateGpaValue(gpaValue)
    if (isValid) {
      this.dispatch(setGraduateGpaValue(value))
    }
  }

  setQuestionAnswer(questionId: number, answer: string) {
    this.dispatch(setQuestionAnswer(questionId, answer))
  }

  setQuestionChoice(questionId: number, options: number[]) {
    this.dispatch(setQuestionChoice(questionId, options))
  }

  parseStateToSubmission(): IUpdateSubmissionDataPayload {
    return {
      enterpriseId: this.state.initiativeData.enterpriseId,
      initiativeId: this.state.initiativeData.id,
      responses: {
        gpaValues: {
          graduateGpa: this.state.answers.gpaValues.graduateGpa,
          undergraduateGpa: this.state.answers.gpaValues.undergraduateGpa
        },
        questionAnswers: Object.entries(this.state.answers.questions).map(([questionId, answer]) => ({
          questionId: parseInt(questionId, 10),
          answer: answer.answer,
          choices: answer.choices
        }))
      }
    }
  }

  async submitChanges(): Promise<[IUserData, string]> {
    this.dispatch(setIsSubmittingInitiative(true))

    const [result, , error] = await Http.put<IUpdateSubmissionDataPayload, { data: IUserData }>(
      apiUrls.enterprise.submission(),
      this.parseStateToSubmission()
    )

    this.dispatch(setIsSubmittingInitiative(false))

    if (error) {
      return [null, error]
    }

    return [result.data, null]
  }

  async withdrawFromInitiative(): Promise<string | null> {
    this.dispatch(setIsWithdrawing(true))

    const { enterpriseId, id: initiativeId } = this.state.initiativeData

    const [, , error] = await Http.$delete<{ data: IUserData }>(
      apiUrls.enterprise.withdrawFromInitiative(enterpriseId, initiativeId)
    )

    this.dispatch(setIsWithdrawing(false))

    return error
  }

  async withdrawFromEnterprise(): Promise<string | null> {
    this.dispatch(setIsWithdrawing(true))

    const { enterpriseId } = this.state.initiativeData

    const [, , error] = await Http.$delete<{ data: IUserData }>(apiUrls.enterprise.withdrawFromEnterprise(enterpriseId))

    this.dispatch(setIsWithdrawing(false))

    return error
  }

  setDoesNotApplyForGraduateGpa(payload: boolean) {
    this.dispatch(setDoesNotApplyGraduateGpa(payload))
    if (payload) {
      this.dispatch(setGraduateGpaValue(null))
    }
  }

  setDoesNotApplyForUndergraduateGpa(payload: boolean) {
    this.dispatch(setDoesNotApplyUndergraduateGpa(payload))
    if (payload) {
      this.dispatch(setUndergraduateGpaValue(null))
    }
  }

  reset() {
    this.dispatch(reset())
  }
}

let instance: InitiativeManagementService = null

export default (() => {
  if (!instance) {
    instance = new InitiativeManagementService()
  }
  return instance
})()
